/**
 * Licence → implementation-service attach map.
 *
 * Professional-services engagements pass through distribution at cost, so they
 * are never priced on the site. What they are worth is the attach: someone
 * buying SentinelOne licences also needs SentinelOne deployed. This map is what
 * puts that offer in front of them on the licence page itself.
 *
 * Keyed by `store_product_families.slug`. Kept in code rather than D1 so the
 * link is part of the page, not editable content that can drift out of sync
 * with the service pages it points at.
 */

export interface DeploymentService {
  /** Service page to link to. */
  href: string;
  /** Heading shown on the licence page. */
  title: string;
  /** One sentence on what the engagement covers. */
  body: string;
  /** Link text. */
  linkLabel: string;
}

const DEPLOYMENT_SERVICES: Record<string, DeploymentService> = {
  sentinelone: {
    href: '/services/security-software-deployment',
    title: 'Need SentinelOne deployed?',
    body: 'Console build-out, agent rollout, policy tuning, and clean removal of your incumbent endpoint product — scoped and quoted as a fixed-price project.',
    linkLabel: 'Security software deployment',
  },
  'proofpoint-essentials': {
    href: '/services/security-software-deployment',
    title: 'Need Proofpoint deployed?',
    body: 'Domain onboarding, mail-flow and connector configuration, quarantine and digests, and a coexistence period so nothing legitimate disappears at cutover.',
    linkLabel: 'Security software deployment',
  },
  'blackpoint-cyber': {
    href: '/services/security-software-deployment',
    title: 'Need Blackpoint Cyber onboarded?',
    body: 'Agent deployment, identity integration, and escalation paths agreed and tested — so isolation actually reaches your endpoints when it matters.',
    linkLabel: 'Security software deployment',
  },
  'check-point-harmony-email': {
    href: '/services/security-software-deployment',
    title: 'Need Harmony Email deployed?',
    body: 'API connection to Microsoft 365 or Google Workspace, phased rollout from monitor to protect, and policy tuning against your real inbound traffic.',
    linkLabel: 'Security software deployment',
  },
  'defender-for-endpoint': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Licensed but not onboarded?',
    body: 'A fixed-scope Defender for Endpoint baseline: device onboarding, security baselines, attack-surface-reduction rules, and alert routing that reaches a human.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'defender-for-office-365': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Want the baseline configured properly?',
    body: 'A fixed-scope Defender for Office 365 baseline covering malicious attachments, links, and spoofing — configured against CIS guidance for Office 365.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'defender-for-business': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Want it configured, not just licensed?',
    body: 'Fixed-scope implementation of the Defender, Intune, Entra, and Purview features your Microsoft 365 licensing already includes.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'microsoft-defender-suite': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Want it configured, not just licensed?',
    body: 'Fixed-scope Defender baselines for endpoint and Office 365, plus Intune, Entra, and Purview configuration, rolled out in stages with a documented handover.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'microsoft-intune': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Need Intune actually stood up?',
    body: 'A full cloud endpoint solution in Intune for corporate Windows and BYOD iOS and Android, built on Microsoft best practices and security baselines.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'microsoft-entra-id': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Need conditional access designed?',
    body: 'Conditional access, MFA enforcement, and privileged-role hygiene built in report-only mode and rolled out in waves, so nobody gets locked out on go-live day.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'microsoft-purview': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Need labelling and DLP configured?',
    body: 'Sensitivity labelling, retention, and data-loss-prevention policies applied across Exchange Online, SharePoint, OneDrive, and Teams as a fixed-scope project.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'microsoft-365': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Business Premium security switched on?',
    body: 'Business Premium includes Defender, Intune, Entra ID, and Purview features most tenants never configure. We implement them as one coherent build.',
    linkLabel: 'Microsoft 365 security implementation',
  },
  'microsoft-copilot': {
    href: '/services/microsoft-365-security-implementation',
    title: 'Rolling out Copilot?',
    body: 'Copilot surfaces everything a user can already reach. Permission cleanup and sensitivity labelling belong before the rollout, not after.',
    linkLabel: 'Microsoft 365 security implementation',
  },
};

export function getDeploymentService(familySlug: string): DeploymentService | undefined {
  return DEPLOYMENT_SERVICES[familySlug];
}
